import { useEffect, useState } from 'react';

import type { HubConnection } from '@microsoft/signalr';

import { type HubConnectionState } from './types';

const initialState: HubConnectionState = {
  connection: undefined,
  error: undefined
};

export function useHubConnection(
  createConnection: () => HubConnection,
  enabled = true
): HubConnectionState {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let connection: HubConnection | null = null;

    // Defer creating the connection by a tick so a fast unmount/remount
    // (StrictMode) cancels the work before any network call goes out.
    const startPromise = Promise.resolve().then(() => {
      if (cancelled) return;
      connection = createConnection();
      return connection
        .start()
        .then(() => {
          if (cancelled) return;
          setState({ connection: connection as HubConnection });
        })
        .catch((error) => {
          if (cancelled) return;
          const message = String(error?.message ?? '');
          // StrictMode aborts surface as this; ignore them.
          if (message.includes('stopped during negotiation')) return;
          setState({ error });
        });
    });

    return () => {
      cancelled = true;
      // Wait for start() to settle before stopping. Calling stop() while
      // negotiate is in flight rejects start() and leaves the hub broken.
      startPromise.finally(() => {
        if (!connection) return;
        connection.stop().catch(() => {
          /* swallow: connection may already be stopped */
        });
      });
    };
  }, [createConnection, enabled]);

  return state;
}
